import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Star,
  Users,
  CheckCircle2,
  Clock,
  Share2,
  ShieldCheck,
  Store as StoreIcon,
  PackageSearch,
  Copy,
  Check,
  CalendarDays,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";

import api, { API } from "../lib/api";
import ProductCard from "../components/ProductCard";

const BACKEND = API.replace(/\/api$/, "");
const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
};

const formatMemberSince = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "—";
  }
};

const formatDelivery = (minutes) => {
  if (minutes === null || minutes === undefined) return "±5 menit";
  if (minutes < 1) return "< 1 menit";
  if (minutes < 60) return `${minutes} menit`;
  const hours = Math.round(minutes / 60);
  return `${hours} jam`;
};

export default function StoreProfile() {
  const { sellerId } = useParams();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    api
      .get(`/store/${sellerId}`)
      .then((r) => {
        if (active) {
          setStore(r.data);
          // Update dokumen title agar tab browser menampilkan nama toko
          // + set meta description sederhana untuk share preview.
          if (r.data?.shop_name) {
            document.title = `${r.data.shop_name} — Toko Resmi di Ustore`;
          }
        }
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [sellerId]);

  const productTypes = useMemo(() => {
    if (!store?.products) return [];
    return Array.from(new Set(store.products.map((p) => p.type).filter(Boolean)));
  }, [store]);

  const visibleProducts = useMemo(() => {
    if (!store?.products) return [];
    if (filter === "all") return store.products;
    return store.products.filter((p) => p.type === filter);
  }, [store, filter]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: store?.shop_name, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link toko disalin");
      }
    } catch {
      /* user cancelled */
    }
  };

  const handleCopyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link toko disalin ke clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Gagal menyalin link");
    }
  };

  if (loading) {
    return (
      <div className="text-white/50 text-center py-32" data-testid="store-loading">
        <StoreIcon className="w-10 h-10 mx-auto mb-3 text-white/30 animate-pulse" />
        Memuat toko...
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center" data-testid="store-not-found">
        <StoreIcon className="w-12 h-12 mx-auto text-white/30 mb-4" />
        <h1 className="font-display font-black text-2xl text-white">Toko tidak ditemukan</h1>
        <p className="text-white/50 mt-2">Toko yang kamu cari tidak tersedia atau sudah tidak aktif.</p>
        <Link to="/browse" className="btn-primary inline-flex mt-6">Jelajahi Produk</Link>
      </div>
    );
  }

  const rating = store.rating || { average: 0, total: 0, breakdown: {} };
  const stats = store.stats || {};
  const maxRatingBar = Math.max(1, ...Object.values(rating.breakdown || {}));

  return (
    <div data-testid="store-profile-page" className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
      {/* ---------- HERO / BANNER ---------- */}
      <div className="relative overflow-hidden border border-white/10 bg-[#0a0a0a">
        {/* neon gradient backdrop */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#00F0FF]/10 via-transparent to-[#7C4DFF]/10 pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#00F0FF]/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-[#7C4DFF]/10 blur-3xl pointer-events-none" />

        {/* Banner "Toko Tutup" — muncul jika seller menutup toko via dashboard.
            Backend mengirim ``is_open`` yang sudah mempertimbangkan
            ``shop_open`` + ``operational_hours``. Saat tutup, tampilkan
            banner besar merah di atas hero dan blokir pembelian di UI. */}
        {!store.is_open && (
          <div
            data-testid="store-closed-banner"
            className="relative border-b-2 border-[#FF003C] bg-gradient-to-r from-[#FF003C]/20 via-[#FF003C]/10 to-[#FF003C]/20 backdrop-blur"
          >
            <div className="flex items-center justify-center gap-3 py-3 px-4 text-center">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF003C] opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#FF003C]" />
              </span>
              <p className="font-display font-black uppercase tracking-widest text-sm md:text-base text-white">
                Toko sedang tutup — produk tidak bisa dibeli sampai toko dibuka kembali
              </p>
            </div>
          </div>
        )}

        <div className="relative p-6 md:p-10">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            {/* Avatar */}
            <div className="shrink-0">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full p-[3px] bg-gradient-to-br from-[#00F0FF] to-[#7C4DFF]">
                <div className="w-full h-full rounded-full bg-black overflow-hidden flex items-center justify-center">
                  {store.avatar ? (
                    <img src={resolveImg(store.avatar)} alt={store.shop_name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-display font-black text-3xl text-[#00F0FF]">
                      {store.shop_name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Identity */}
            <div className="flex-1 min-w-0">
              <span className="tag tag-accent">// TOKO RESMI</span>
              <div className="flex items-center gap-3 flex-wrap mt-2">
                <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white truncate" data-testid="store-name">
                  {store.shop_name}
                </h1>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#00F0FF]/10 border border-[#00F0FF]/40 text-[#00F0FF] text-[10px] font-bold uppercase tracking-widest">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
                <button
                  onClick={handleShare}
                  data-testid="store-share-btn"
                  className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/70 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"
                  aria-label="Bagikan toko"
                  title="Bagikan toko"
                >
                  <Share2 className="w-4 h-4" />
                </button>
                <button
                  onClick={handleCopyLink}
                  data-testid="store-copy-link-btn"
                  className="w-9 h-9 flex items-center justify-center border border-white/15 text-white/70 hover:border-[#7C4DFF] hover:text-[#7C4DFF] transition-colors"
                  aria-label="Salin link toko"
                  title="Salin link toko"
                >
                  {copied ? <Check className="w-4 h-4 text-[#00FF66]" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/55">
                <span className="flex items-center gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5 text-[#00F0FF]" />
                  Toko dibuka sejak {formatMemberSince(store.created_at)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-[#00F0FF]" /> Jam Operasional: {store.operational_hours}
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wide ${
                    store.is_open
                      ? "bg-[#00FF66]/10 text-[#00FF66] border border-[#00FF66]/40"
                      : "bg-white/5 text-white/50 border border-white/15"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${store.is_open ? "bg-[#00FF66]" : "bg-white/40"}`} />
                  {store.is_open ? "Toko Buka" : "Toko Tutup"}
                </span>
              </div>

              {store.description && (
                <p className="mt-4 text-white/60 text-sm max-w-2xl leading-relaxed">{store.description}</p>
              )}
            </div>
          </div>

          {/* Link publik toko (bisa dicopas oleh siapa saja) */}
          <div className="mt-6 pt-5 border-t border-white/10">
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/45 block mb-2">Link Toko Resmi</label>
            <div className="flex items-center gap-2 max-w-2xl">
              <div className="flex-1 flex items-center gap-2 bg-black border border-white/15 px-3 py-2 min-w-0">
                <StoreIcon className="w-3.5 h-3.5 text-[#00F0FF] shrink-0" />
                <span className="text-xs text-white/70 truncate font-mono" data-testid="store-public-link">
                  {typeof window !== "undefined" ? window.location.href : ""}
                </span>
              </div>
              <button
                type="button"
                onClick={handleCopyLink}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 text-[10px] uppercase font-bold tracking-widest"
                data-testid="store-public-link-copy"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Tersalin" : "Copy"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- STATS + RATING ---------- */}
      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        {/* Transaksi */}
        <div className="lg:col-span-2 border border-white/10 bg-[#0a0a0a] p-6">
          <h2 className="text-xs uppercase font-bold tracking-[0.2em] text-white/50 mb-5">Statistik Toko</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <StatItem
              icon={Users}
              label="Pembeli"
              value={`${stats.total_buyers || 0} Orang`}
              hint={`${stats.recent_buyers || 0} (2 minggu terakhir)`}
            />
            <StatItem
              icon={CheckCircle2}
              label="Terjual"
              value={`${stats.sold_percentage || 0}%`}
              hint={`${stats.total_sold || 0} / ${stats.total_listings || 0} produk`}
            />
            <StatItem
              icon={Clock}
              label="Rata-rata Pengiriman"
              value={formatDelivery(stats.avg_delivery_minutes)}
              hint="Respon cepat"
            />
          </div>
        </div>

        {/* Rating */}
        <div className="border border-white/10 bg-[#0a0a0a] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs uppercase font-bold tracking-[0.2em] text-white/50">Rating Toko</h2>
            <div className="flex items-center gap-1.5">
              <Star className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
              <span className="font-display font-black text-white text-lg leading-none" data-testid="store-rating-average">
                {(rating.average || 0).toFixed(1)}
              </span>
              <span className="text-white/40 text-sm">/ 5.0</span>
            </div>
          </div>
          <div className="space-y-1.5">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = rating.breakdown?.[star] || 0;
              const pct = (count / maxRatingBar) * 100;
              return (
                <div key={star} className="flex items-center gap-2" data-testid={`rating-bar-${star}`}>
                  <div className="flex items-center gap-0.5 w-16 shrink-0">
                    {Array.from({ length: star }).map((_, i) => (
                      <Star key={i} className="w-2.5 h-2.5 fill-[#FFD700] text-[#FFD700]" />
                    ))}
                  </div>
                  <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#00F0FF] to-[#7C4DFF] rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-6 text-right text-xs text-white/50">{count}</span>
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-[11px] text-white/40" data-testid="store-rating-total">
            {rating.total || 0} ulasan pembeli
          </p>
        </div>
      </div>

      {/* ---------- PRODUCTS ---------- */}
      <div className="mt-10">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
          <div>
            <span className="tag tag-accent">// KATALOG TOKO</span>
            <h2 className="font-display font-black text-2xl md:text-3xl text-white mt-2" data-testid="store-products-title">
              Semua Produk ({store.products?.length || 0})
            </h2>
            <p className="text-white/50 text-xs md:text-sm mt-1">
              Hanya produk dengan stok aktif yang ditampilkan.
            </p>
          </div>
          {productTypes.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="store-filter">
              <FilterPill active={filter === "all"} onClick={() => setFilter("all")}>
                Semua
              </FilterPill>
              {productTypes.map((t) => (
                <FilterPill key={t} active={filter === t} onClick={() => setFilter(t)}>
                  {t}
                </FilterPill>
              ))}
            </div>
          )}
        </div>

        {visibleProducts.length === 0 ? (
          <div className="border border-white/10 bg-[#0a0a0a] py-20 text-center" data-testid="store-empty">
            <PackageSearch className="w-10 h-10 mx-auto text-white/25 mb-3" />
            <p className="text-white/50">Belum ada produk di kategori ini.</p>
            <p className="text-white/30 text-xs mt-1">Coba pilih kategori lain di atas, atau kembali nanti.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
            {visibleProducts.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>

      {/* ---------- FOOTER NOTE ---------- */}
      <div className="mt-10 pt-6 border-t border-white/10 text-center">
        <p className="text-white/30 text-[11px] uppercase tracking-widest">
          Halaman publik toko · Ustore Marketplace
        </p>
      </div>
    </div>
  );
}

function StatItem({ icon: Icon, label, value, hint }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#00F0FF]/10 border border-[#00F0FF]/30">
        <Icon className="w-4 h-4 text-[#00F0FF]" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">{label}</div>
        <div className="font-display font-bold text-white text-lg leading-tight mt-0.5">{value}</div>
        {hint && <div className="text-[11px] text-white/40 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold border transition-colors ${
        active
          ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10"
          : "border-white/15 text-white/50 hover:text-white/80 hover:border-white/30"
      }`}
    >
      {children}
    </button>
  );
}
